/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { notFound, redirect } from '@tanstack/react-router'
import { authQueryKeys } from '@web/src/hooks/use-auth'
import {
  accountLoginRedirectHref,
  accountShortcutDestinationHref,
  accountShortcutUnauthenticatedRedirectTo,
  resolveAccountShortcut,
} from '@web/src/lib/account/account-shortcut'
import type { BetterAuthSessionData, SessionSession, SessionUser } from '@web/src/lib/yoga/yoga-auth-context'

type QueryClientLike = {
  getQueryData: (key: readonly unknown[]) => unknown
}

export function redirectAccountShortcut({
  context,
  section,
}: {
  context: { queryClient: QueryClientLike }
  section?: string
}): never {
  const resolution = resolveAccountShortcut(section)
  if (resolution.kind === 'not-found') {
    throw notFound()
  }

  const session = context.queryClient.getQueryData(authQueryKeys.session) as BetterAuthSessionData | undefined
  const currentUser = session?.user as SessionUser | undefined
  const currentSession = session?.session as SessionSession | undefined
  const currentProfileId = currentUser?.profile?.id

  if (!currentSession) {
    throw redirect({
      href: accountLoginRedirectHref(accountShortcutUnauthenticatedRedirectTo(resolution)),
    })
  }

  const href = accountShortcutDestinationHref({ resolution, currentProfileId })
  if (!href) {
    throw redirect({ href: accountLoginRedirectHref('/account') })
  }

  throw redirect({ href })
}
